export interface ActiveSectionBounds {
  id: string;
  /** Viewport-relative top of the section, in CSS pixels. */
  top: number;
  /** Viewport-relative bottom of the section, in CSS pixels. */
  bottom: number;
}

type ReadingBand = { top: number; bottom: number };

function bandOverlap(section: ActiveSectionBounds, band: ReadingBand): number {
  return Math.min(section.bottom, band.bottom) - Math.max(section.top, band.top);
}

/**
 * The section being read, given sections in document order and the viewport-relative reading
 * band: the one covering most of the band (the earlier one on a tie), so the last pixels of the
 * previous section never win after an anchor jump; otherwise the last one whose top is above the
 * band's bottom edge (the reader is past it); otherwise, above every section, the first one.
 */
export function resolveActiveSection(
  sections: readonly ActiveSectionBounds[],
  band: ReadingBand,
): string | null {
  const covering = sections.reduce<ActiveSectionBounds | null>(
    (best, section) =>
      bandOverlap(section, band) > (best ? bandOverlap(best, band) : 0) ? section : best,
    null,
  );
  if (covering) return covering.id;
  const passed = sections.findLast((section) => section.top <= band.bottom);
  return passed?.id ?? sections[0]?.id ?? null;
}
